import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiClient, { marketService } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Countdown from '../components/Countdown';
import WinnersDisplay from '../components/WinnersDisplay';
import MarketGraph from '../components/MarketGraph';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, User, Calendar, CheckCircle, XCircle, Ban, Tag, Lock, Globe, MessageSquare, Send, Share2, Wallet, Edit, Plus, Search, X, UserCheck, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const MarketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [market, setMarket] = useState(null);
  const [userBets, setUserBets] = useState([]);
  const [winners, setWinners] = useState([]);
  const [marketBets, setMarketBets] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [betAmount, setBetAmount] = useState(1.00);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showBetModal, setShowBetModal] = useState(false);
  const [betTargetMarket, setBetTargetMarket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [joining, setJoining] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [linkToCopy, setLinkToCopy] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);

  // Specific Users Manage State
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [tempAllowedUsers, setTempAllowedUsers] = useState([]);

  // Edit State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editVisibilityType, setEditVisibilityType] = useState('public');
  const [editSelectedGroup, setEditSelectedGroup] = useState('');
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    fetchMarketData();
    fetchGroups();

    // Listen for market updates from SSE
    const handleMarketUpdate = (event) => {
      const updatedMarket = event.detail;
      if (updatedMarket.id === id) {
        setMarket(prev => ({
          ...prev,
          ...updatedMarket
        }));
      }
    };

    window.addEventListener('market_updated', handleMarketUpdate);

    // Poll for market and comments data every 30 seconds (increased from 10 to reduce server load)
    const interval = setInterval(() => {
      fetchMarketData(true);
      fetchComments();
    }, 30000);
    return () => {
      clearInterval(interval);
      window.removeEventListener('market_updated', handleMarketUpdate);
    };
  }, [id]);

  const fetchMarketData = async (isPoll = false) => {
    try {
      if (!isPoll) setLoading(true);
      // First fetch the market itself to avoid 404 on sub-requests triggering "Market not found"
      const marketRes = await marketService.getById(id);
      
      // Update market state
      setMarket(marketRes.data);
      setBetTargetMarket(marketRes.data);
      
      if (!isPoll) {
        // Update betAmount to at least the last_bet_amount
        setBetAmount(prev => Math.max(prev, marketRes.data.last_bet_amount || 1.00));
      }

      // Sequential fetch remaining data
      const [userBetsRes, marketBetsRes, commentsRes] = await Promise.all([
        apiClient.get(`/bets/market/${id}`),
        apiClient.get(`/markets/${id}/bets`),
        apiClient.get(`/markets/${id}/comments`)
      ]);

      setUserBets(userBetsRes.data);
      setMarketBets(marketBetsRes.data);
      setComments(commentsRes.data);
      
      if (marketRes.data.status === 'resolved') {
        const winnersRes = await apiClient.get(`/markets/${id}/winners`);
        setWinners(winnersRes.data);
        fetchPayouts();
      }
    } catch (error) {
      if (!isPoll) {
        console.error('Failed to fetch market:', error);
        toast.error(t('marketNotFound'));
      }
    } finally {
      if (!isPoll) setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiClient.get(`/groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await apiClient.get(`/markets/${id}/comments`);
      setComments(res.data);
    } catch (e) {
      console.error('Failed to fetch comments');
    }
  };

  const fetchPayouts = async () => {
    try {
      setPayoutsLoading(true);
      const res = await apiClient.get(`/markets/${id}/payouts`);
      setPayouts(res.data);
    } catch (e) {
      console.error('Failed to fetch payouts');
    } finally {
      setPayoutsLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get(`/users/search?q=${query}`);
      setUserSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const handleUserSearch = (value) => {
    setUserSearch(value);
    searchUsers(value);
  };

  const addAllowedUser = (u) => {
    if (!tempAllowedUsers.find(user => user.id === u.id)) {
      setTempAllowedUsers([...tempAllowedUsers, { id: u.id, username: u.username }]);
    }
    setUserSearch('');
    setUserSearchResults([]);
  };

  const removeAllowedUser = (userId) => {
    setTempAllowedUsers(tempAllowedUsers.filter(u => u.id !== userId));
  };

  const saveAllowedUsers = async () => {
    setSubmitting(true);
    try {
      const newConfig = {
        ...market.visibility_config,
        allowed_users: tempAllowedUsers.map(u => u.id)
      };
      await marketService.update(id, { visibility_config: newConfig });
      toast.success(language === 'de' ? 'Berechtigungen aktualisiert' : 'Permissions updated');
      setShowManageUsersModal(false);
      fetchMarketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const updateMarket = async () => {
    if (!editTitle || editTitle.length < 5) {
      toast.error(language === 'de' ? 'Titel muss mindestens 5 Zeichen haben' : 'Title must be at least 5 characters');
      return;
    }

    setSubmitting(true);
    try {
      await marketService.update(id, {
        title: editTitle,
        description: editDescription,
        visibility_type: editVisibilityType,
        group_id: editVisibilityType === 'group' ? editSelectedGroup : null
      });
      toast.success(language === 'de' ? 'Wette aktualisiert' : 'Market updated');
      setShowEditModal(false);
      fetchMarketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update market');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPayout = async (payoutId) => {
    try {
      await apiClient.post('/payouts/confirm', { payout_id: payoutId });
      toast.success(language === 'de' ? 'Zahlung bestätigt!' : 'Payout confirmed!');
      fetchPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirmation failed');
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await apiClient.post(`/markets/${id}/comments`, { comment: newComment });
      setNewComment('');
      fetchComments();
    } catch (e) {
      toast.error('Failed to post comment');
    }
  };

  const shareMarket = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: market.title,
        url: url
      }).catch(console.error);
    } else {
      copyToClipboard(url, language === 'de' ? 'Link kopiert!' : 'Link copied!');
    }
  };

  const handleBet = (position, targetMarket = market) => {
    setSelectedPosition(position);
    setBetTargetMarket(targetMarket);
    setBetAmount(prev => Math.max(prev, targetMarket?.last_bet_amount || 1.00));
    setShowBetModal(true);
  };

  const submitBet = async () => {
    const amountFloat = parseFloat(betAmount);
    if (isNaN(amountFloat) || amountFloat <= 0) {
      toast.error(t('invalidBetAmount'));
      return;
    }

    const minAllowed = Math.max(1.00, betTargetMarket?.last_bet_amount || 1.00);
    if (amountFloat < minAllowed) {
      toast.error(language === 'de' 
        ? `Einsatz muss mindestens ${minAllowed.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € sein.` 
        : `Einsatz muss mindestens ${minAllowed.toLocaleString('en-US', { minimumFractionDigits: 2 })} € sein.`);
      return;
    }

    if (amountFloat > 50.0) {
      toast.error(t('maxBetError'));
      return;
    }

    const targetId = betTargetMarket?.id || id;

    setSubmitting(true);
    try {
      await marketService.bet(targetId, {
        position: selectedPosition,
        amount: amountFloat,
      });
      toast.success(`${t('betPlaced')}: ${parseFloat(betAmount).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € ${language === 'de' ? 'auf' : 'on'} ${selectedPosition.toUpperCase()}`);
      setShowBetModal(false);
      await fetchMarketData();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('betPlacementFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveMarket = async (winner) => {
    setSubmitting(true);
    try {
      await marketService.resolve(id, { winner });
      toast.success(t('marketResolvedSuccess'));
      setShowResolveModal(false);
      
      // Trigger confetti animation
      if (typeof window !== 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
        script.onload = () => {
          window.confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#ef4444', '#ffffff', '#22c55e']
          });
        };
        document.body.appendChild(script);
      }

      await fetchMarketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve market');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMarket = async () => {
    setSubmitting(true);
    try {
      await marketService.cancel(id);
      toast.success(t('marketCancelledSuccess'));
      setShowCancelModal(false);
      await fetchMarketData();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel market');
    } finally {
      setSubmitting(false);
    }
  };

  const joinMarket = async () => {
    setJoining(true);
    try {
      await marketService.join(id);
      toast.success(language === 'de' ? 'Erfolgreich beigetreten!' : 'Successfully joined!');
      await fetchMarketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join market');
    } finally {
      setJoining(false);
    }
  };

  const copyInviteLink = () => {
    const url = window.location.href;
    copyToClipboard(url, language === 'de' ? 'Einladungslink kopiert!' : 'Invite link copied!');
  };

  const copyToClipboard = async (text, successMessage) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(successMessage);
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (error) {
      console.warn('Modern clipboard API failed, trying fallback...', error);
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const success = document.execCommand('copy');
        if (success) {
          toast.success(successMessage);
        } else {
          setLinkToCopy(text);
          setShowCopyDialog(true);
        }
      } catch (err) {
        console.error('Fallback copy failed', err);
        setLinkToCopy(text);
        setShowCopyDialog(true);
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="app-container" data-testid="market-detail-loading">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!market) {
    return (
      <div className="app-container" data-testid="market-not-found">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <h1 className="text-2xl font-bold mb-4">{t('marketNotFound')}</h1>
          <Link to="/markets">
            <Button>{t('backToMarkets')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const totalPool = (market?.yes_pool || 0) + (market?.no_pool || 0);
  const yesPercent = totalPool > 0 ? ((market?.yes_pool || 0) / totalPool) * 100 : 50;
  const noPercent = totalPool > 0 ? ((market?.no_pool || 0) / totalPool) * 100 : 50;

  const calculatePotentialWin = (amount, position, isCurrentBet = false, targetMarket = market) => {
    const numAmount = parseFloat(amount) || 0;
    if (numAmount <= 0) return 0;
 
    const currentYesPool = targetMarket?.yes_pool || 0;
    const currentNoPool = targetMarket?.no_pool || 0;
    const totalCurrentPool = currentYesPool + currentNoPool;
 
    if (position === 'yes') {
      const newYesPool = isCurrentBet ? currentYesPool : currentYesPool + numAmount;
      const share = numAmount / newYesPool;
      return share * (isCurrentBet ? totalCurrentPool : totalCurrentPool + numAmount);
    } else {
      const newNoPool = isCurrentBet ? currentNoPool : currentNoPool + numAmount;
      const share = numAmount / newNoPool;
      return share * (isCurrentBet ? totalCurrentPool : totalCurrentPool + numAmount);
    }
  };

  const renderGroupMembers = () => {
    if (!market?.group_members || market.group_members.length === 0) return null;
    return (
      <Card className="border-border/50">
        <CardHeader className="p-4 sm:p-6 pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-red-500" />
            {language === 'de' ? 'Gruppenmitglieder' : 'Group Members'}
            <Badge variant="secondary" className="ml-auto">{market.group_members.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="flex flex-wrap gap-2">
            {market.group_members.map(member => (
              <Link key={member.id} to={`/user/${member.id}`}>
                <Badge variant="outline" className="hover:bg-secondary cursor-pointer py-1" title={`@${member.username}`}>
                  {member.nickname ? `${member.nickname} (@${member.username})` : `@${member.username}`}
                </Badge>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAllowedUsers = () => {
    if (market?.visibility_type !== 'specific_users') return null;
    const allowed = market?.allowed_users || [];
    return (
      <Card className="border-border/50">
        <CardHeader className="p-4 sm:p-6 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-yellow-500" />
              {language === 'de' ? 'Berechtigte Personen' : 'Authorized Users'}
              <Badge variant="secondary" className="ml-auto">{allowed.length}</Badge>
            </CardTitle>
            <div className="flex gap-2">
              {isCreator && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 text-xs gap-1 border-primary/30 hover:bg-primary/5"
                    onClick={copyInviteLink}
                  >
                    <Share2 className="w-3 h-3" />
                    {language === 'de' ? 'Link' : 'Link'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-xs gap-1"
                    onClick={() => {
                      setTempAllowedUsers(allowed);
                      setShowManageUsersModal(true);
                    }}
                  >
                    <Plus className="w-3 h-3" />
                    {language === 'de' ? 'Bearbeiten' : 'Edit'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="flex flex-wrap gap-2">
            {allowed.length > 0 ? allowed.map(u => (
              <Link key={u.id} to={`/user/${u.id}`}>
                <Badge variant="outline" className="hover:bg-secondary cursor-pointer py-1" title={`@${u.username}`}>
                  {u.nickname ? `${u.nickname} (@${u.username})` : `@${u.username}`}
                </Badge>
              </Link>
            )) : (
              <p className="text-xs text-muted-foreground italic">
                {language === 'de' ? 'Noch niemand hinzugefügt.' : 'No one added yet.'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTieredTable = () => {
    if (!market?.tiered_markets || market.tiered_markets.length === 0) return null;

    return (
      <Card className="border-border/50 overflow-hidden shadow-lg animate-fade-in">
        <CardHeader className="bg-gradient-to-r from-blue-600/10 via-primary/5 to-blue-600/10 border-b border-border/10 p-4 sm:p-6">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            {t('tieredMarkets')}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {language === 'de'
              ? 'Wähle eine Schwelle aus oder wette direkt auf JA oder NEIN.'
              : 'Select a threshold or bet directly on YES or NO.'}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border/50 text-[10px] sm:text-xs uppercase font-bold tracking-wider text-muted-foreground bg-secondary/10">
                  <th className="p-3 sm:p-4">{t('threshold')}</th>
                  <th className="p-3 sm:p-4 text-center">JA</th>
                  <th className="p-3 sm:p-4 text-center">NEIN</th>
                  <th className="p-3 sm:p-4 text-center">{language === 'de' ? 'Wetten' : 'Bet'}</th>
                  <th className="p-3 sm:p-4 text-right">{language === 'de' ? 'Status' : 'Status'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30 text-xs sm:text-sm font-medium">
                {market.tiered_markets.map((tm) => {
                  const tmTotalPool = (tm.yes_pool || 0) + (tm.no_pool || 0);
                  const tmYesPercent = tmTotalPool > 0 ? ((tm.yes_pool || 0) / tmTotalPool) * 100 : 50;
                  const tmNoPercent = 100 - tmYesPercent;
                  
                  const isCurrent = tm.id === id;
                  const tmEndDate = tm.end_date ? new Date(tm.end_date) : null;
                  const tmIsEnded = tmEndDate ? tmEndDate <= new Date() : false;
                  const tmCanBet = tm.status === 'open' && !tmIsEnded && tm.user_bet_count === 0;

                  return (
                    <tr 
                      key={tm.id} 
                      className={cn(
                        "transition-all duration-200 hover:bg-secondary/20 cursor-pointer",
                        isCurrent ? "bg-primary/5 border-l-4 border-l-primary" : ""
                      )}
                      onClick={() => {
                        if (!isCurrent) navigate(`/market/${tm.id}`);
                      }}
                    >
                      <td className="p-3 sm:p-4 font-bold flex flex-wrap items-center gap-2">
                        <span className="truncate">{tm.title}</span>
                        {isCurrent && (
                          <Badge variant="secondary" className="text-[9px] px-1 py-0 bg-primary/20 text-primary border-primary/20 w-fit shrink-0">
                            {t('currentTier')}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 sm:p-4 text-center font-mono font-bold text-green-400">
                        {tmYesPercent.toFixed(0)}%
                      </td>
                      <td className="p-3 sm:p-4 text-center font-mono font-bold text-red-400">
                        {tmNoPercent.toFixed(0)}%
                      </td>
                      <td className="p-2 sm:p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        {tmCanBet ? (
                          <div className="flex justify-center gap-1 sm:gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 px-2 text-[10px] font-black border-green-500/20 hover:border-green-500 bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white"
                              onClick={() => handleBet('yes', tm)}
                            >
                              JA
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 px-2 text-[10px] font-black border-red-500/20 hover:border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white"
                              onClick={() => handleBet('no', tm)}
                            >
                              NEIN
                            </Button>
                          </div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground text-center italic">
                            {tm.user_bet_count > 0 
                              ? (language === 'de' ? 'Bereits getippt' : 'Bereits getippt') 
                              : (language === 'de' ? 'Geschlossen' : 'Closed')}
                          </div>
                        )}
                      </td>
                      <td className="p-3 sm:p-4 text-right">
                        {tm.status === 'resolved' ? (
                          <Badge className={tm.result === 'yes' ? 'bg-green-600' : 'bg-red-600'}>
                            {tm.result === 'yes' ? 'JA' : 'NEIN'}
                          </Badge>
                        ) : tm.status === 'cancelled' ? (
                          <Badge variant="outline" className="border-red-500/20 text-red-400 bg-red-500/10">
                            X
                          </Badge>
                        ) : tmIsEnded ? (
                          <Badge variant="secondary" className="text-[10px]">
                            {language === 'de' ? 'Wartet' : 'Waiting'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-green-500/20 text-green-400 bg-green-500/10 animate-pulse text-[10px]">
                            {language === 'de' ? 'Aktiv' : 'Active'}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  const potentialWin = calculatePotentialWin(betAmount, selectedPosition, false, betTargetMarket || market);
  const endDate = market?.end_date ? new Date(market.end_date) : null;
  const isEnded = endDate ? endDate <= new Date() : false;
  const canBet = market?.status === 'open' && !isEnded;
  const hasAlreadyBetted = userBets.length > 0;
  const isCreator = user?.id === market?.creator_id;
  const canEdit = (isCreator || user?.is_admin) && (market?.total_bets === 0 || user?.is_admin);
  const canResolve = (isCreator || user?.is_admin) && market?.status === 'open';
  const canCancel = (isCreator || user?.is_admin) && market?.status === 'open';

  const statusColors = {
    open: 'bg-green-600/20 text-green-400 border-green-600/30',
    closed: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
    resolved: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
    cancelled: 'bg-red-600/20 text-red-400 border-red-600/30',
  };

  const statusText = {
    open: t('statusOpen'),
    closed: t('statusClosed'),
    resolved: t('statusResolved'),
    cancelled: t('statusCancelled'),
  };

  const visibilityIcon = () => {
    const vis = market.visibility?.type;
    if (vis === 'email_domain') return <Lock className="w-4 h-4" />;
    if (vis === 'specific_users') return <Users className="w-4 h-4" />;
    if (vis === 'group') return <Users className="w-4 h-4" />;
    return <Globe className="w-4 h-4" />;
  };

  const renderWithLinks = (text) => {
    if (!text) return null;
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const username = part.substring(1);
        // Find user id if possible, but for now we search by username
        // Simplified: link to a search page or direct profile if we had IDs
        // Since we don't have all IDs here, we link to a placeholder profile page or search
        return (
          <Link 
            key={i} 
            to={`/markets?q=${username}`} 
            className="text-red-500 hover:underline font-medium"
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  return (
    <div className="app-container" data-testid="market-detail-page">

      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Join Overlay for Private Markets */}
        {market.needs_join && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md bg-background/80 animate-in fade-in duration-500">
            <Card className="w-full max-w-md border-primary/20 shadow-2xl shadow-primary/10">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-black italic uppercase italic">{language === 'de' ? 'Private Wette' : 'Private Market'}</CardTitle>
                <p className="text-muted-foreground mt-2">
                  {language === 'de' 
                    ? 'Du wurdest zu dieser privaten Wette eingeladen. Möchtest du beitreten?' 
                    : 'You have been invited to this private market. Do you want to join?'}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-secondary/30 rounded-xl border border-border/5 flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={market.creator_avatar_url} alt={market.creator_username} className="object-cover" />
                    <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-sm">
                      {market.creator_username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-bold text-base">{market.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5" title={`@${market.creator_username}`}>
                      Erstellt von {market.creator_nickname ? `${market.creator_nickname} (@${market.creator_username})` : `@${market.creator_username}`}
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-2">
                <Button 
                  className="w-full h-12 text-lg font-bold" 
                  onClick={joinMarket}
                  disabled={joining}
                >
                  {joining ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <UserCheck className="w-5 h-5 mr-2" />}
                  {language === 'de' ? 'WETTE BEITRETEN' : 'JOIN MARKET'}
                </Button>
                <Link to="/markets" className="w-full">
                  <Button variant="ghost" className="w-full text-muted-foreground">{t('backToMarkets')}</Button>
                </Link>
              </CardFooter>
            </Card>
          </div>
        )}

        {/* Back button */}
        <Link to="/markets" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" />
          {t('backToMarkets')}
        </Link>

        {/* Market Header */}
        <Card className="border-border/50 overflow-hidden">
          <CardHeader className="p-0 overflow-hidden relative">
            {market.image_url && (
              <div className="w-full h-48 sm:h-64 md:h-80 relative">
                <img 
                  src={market.image_url} 
                  alt={market.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
              </div>
            )}
            <div className={cn("p-4 sm:p-6 pb-2 sm:pb-4 space-y-4", market.image_url && "-mt-20 relative z-10")}>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={cn("px-2 py-0 text-[10px] uppercase font-bold tracking-wider", statusColors[market.status])}>
                      {statusText[market.status]}
                    </Badge>
                    {market.resolution && (
                      <Badge className={cn("px-2 py-0 text-[10px] uppercase font-bold tracking-wider", market?.resolution === 'yes' ? 'bg-green-600' : 'bg-red-600')}>
                        {language === 'de' ? 'Ergebnis' : 'Result'}: {market?.resolution === 'yes' ? t('yes') : t('no')}
                      </Badge>
                    )}
                    {market.category && (
                      <Badge variant="secondary" className="px-2 py-0 text-[10px] uppercase font-bold tracking-wider">
                        {market.category}
                      </Badge>
                    )}
                    {market.group_id && (
                      <Badge variant="secondary" className="px-2 py-0 text-[10px] uppercase font-bold tracking-wider bg-purple-500/10 text-purple-400 border-purple-500/20">
                        <Users className="w-3 h-3 mr-1" />
                        {market.group_name || (language === 'de' ? 'Gruppe' : 'Group')}
                      </Badge>
                    )}
                    {canEdit && (
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="h-6 px-2 text-[10px] uppercase font-bold tracking-wider gap-1"
                        onClick={() => {
                          setEditTitle(market.title);
                          setEditDescription(market.description || '');
                          setEditVisibilityType(market.visibility_type || 'public');
                          setEditSelectedGroup(market.group_id || '');
                          setShowEditModal(true);
                        }}
                      >
                        <Edit className="w-3 h-3" />
                        {language === 'de' ? 'Bearbeiten' : 'Edit'}
                      </Button>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={shareMarket} className="shrink-0 h-8 w-8">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight" data-testid="market-title">
                  {renderWithLinks(market.title)}
                </h1>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 sm:p-6 pt-0 space-y-6">
            {market.description && (
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed" data-testid="market-description">
                {renderWithLinks(market.description)}
              </p>
            )}

            {/* Meta info */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-4 gap-y-3 text-xs sm:text-sm text-muted-foreground border-y border-border/10 py-4">
              <div className="flex items-center gap-2">
                <Avatar className="h-5 w-5">
                  <AvatarImage src={market.creator_avatar_url} alt={market.creator_username} className="object-cover" />
                  <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-[10px]">
                    {market.creator_username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate" title={`@${market.creator_username}`}>
                  {market.creator_nickname || market.creator_username}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                <span>{new Date(market.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                <span>{market.total_bets} {t('bets')}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-mono">{(totalPool || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
              </div>
            </div>

            {/* Countdown */}
            <div className="flex flex-col items-center sm:items-start gap-2 bg-secondary/20 p-4 rounded-xl">
              <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">{t('timeRemaining')}</p>
              <Countdown endDate={market.end_date} />
            </div>

            {/* Odds Display */}
            <div className="space-y-6 py-4 bg-background/40 p-4 rounded-2xl border border-white/5 shadow-inner">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center space-y-1 group cursor-default">
                  <p className="text-4xl sm:text-5xl font-black text-green-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)] transition-transform group-hover:scale-110">
                    {yesPercent.toFixed(0)}%
                  </p>
                  <p className="text-[10px] uppercase font-black tracking-[0.2em] text-muted-foreground opacity-70">{t('yes')}</p>
                </div>
                <div className="text-center space-y-1 group cursor-default">
                  <p className="text-4xl sm:text-5xl font-black text-red-400 drop-shadow-[0_0_10px_rgba(239,68,68,0.3)] transition-transform group-hover:scale-110">
                    {noPercent.toFixed(0)}%
                  </p>
                  <p className="text-[10px] uppercase font-black tracking-[0.2em] text-muted-foreground opacity-70">{t('no')}</p>
                </div>
              </div>

              <div className="relative group h-4 sm:h-5">
                <div className="odds-bar h-full rounded-full overflow-hidden shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] border border-white/5">
                  <div className="odds-bar-yes transition-all duration-1000 ease-out relative" style={{ width: `${yesPercent}%` }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 animate-pulse" />
                  </div>
                  <div className="odds-bar-no transition-all duration-1000 ease-out" style={{ width: `${noPercent}%` }} />
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 bg-white/20 blur-[1px] rounded-full z-10" />
              </div>

              <div className="flex justify-between text-[11px] font-black font-mono tracking-widest uppercase text-muted-foreground/80 px-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.5)]" />
                  <span>{(market?.yes_pool || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>{(market?.no_pool || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_5px_rgba(239,68,68,0.5)]" />
                </div>
              </div>
            </div>

            {/* Betting Buttons */}
            {canBet ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  className="btn-yes h-16 sm:h-20 text-xl font-black shadow-[0_0_20px_rgba(34,197,94,0.15)] hover:shadow-[0_0_30px_rgba(34,197,94,0.3)] transition-all duration-300 group relative overflow-hidden"
                  onClick={() => handleBet('yes')}
                  disabled={hasAlreadyBetted}
                  data-testid="bet-yes-btn"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <CheckCircle className="w-6 h-6 mr-2 transition-transform group-hover:scale-110" />
                      {t('betYes')}
                    </div>
                    <span className="text-[10px] opacity-70 font-normal mt-1 uppercase tracking-widest">{yesPercent.toFixed(0)}% Chance</span>
                  </div>
                </Button>
                <Button
                  className="btn-no h-16 sm:h-20 text-xl font-black shadow-[0_0_20px_rgba(239,68,68,0.15)] hover:shadow-[0_0_30px_rgba(239,68,68,0.3)] transition-all duration-300 group relative overflow-hidden"
                  onClick={() => handleBet('no')}
                  disabled={hasAlreadyBetted}
                  data-testid="bet-no-btn"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <XCircle className="w-6 h-6 mr-2 transition-transform group-hover:scale-110" />
                      {t('betNo')}
                    </div>
                    <span className="text-[10px] opacity-70 font-normal mt-1 uppercase tracking-widest">{noPercent.toFixed(0)}% Chance</span>
                  </div>
                </Button>
                {hasAlreadyBetted && (
                  <div className="col-span-full flex items-center justify-center gap-2 p-3 bg-primary/10 rounded-xl border border-primary/20 animate-in fade-in slide-in-from-top-2">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    <p className="text-sm font-bold text-primary">
                      {language === 'de' ? 'Wette platziert. Viel Erfolg!' : 'Bet placed. Good luck!'}
                    </p>
                  </div>
                )}
              </div>
            ) : market.status === 'open' && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-500 text-sm text-center font-medium">
                {t('ended')}
              </div>
            )}

            {/* Resolve Button */}
            {canResolve && (
              <div className={cn(
                "group relative overflow-hidden p-0.5 rounded-2xl transition-all duration-500",
                isEnded 
                  ? "bg-gradient-to-r from-red-500 via-primary to-red-500 animate-shimmer bg-[length:200%_100%] shadow-[0_0_20px_rgba(239,68,68,0.3)]" 
                  : "bg-border/30 hover:bg-border/50"
              )}>
                <div className="bg-card rounded-[calc(1rem-2px)] p-4 space-y-4 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70">
                        {language === 'de' ? 'Admin Bereich' : 'Admin Area'}
                      </p>
                      <h4 className="text-lg font-black uppercase tracking-tighter italic">
                        {t('resolveMarket')}
                      </h4>
                    </div>
                    {isEnded && (
                      <Badge className="bg-red-500 animate-pulse text-[10px] font-black uppercase tracking-widest px-2 py-0.5">
                        {language === 'de' ? 'Zeit Abgelaufen' : 'Time Expired'}
                      </Badge>
                    )}
                  </div>

                  {isEnded && (
                    <p className="text-xs font-medium text-muted-foreground leading-relaxed italic border-l-2 border-red-500 pl-3">
                      {language === 'de' 
                        ? 'Die Wette ist beendet. Wähle das Ergebnis, um die Gewinne auszuzahlen.' 
                        : 'The market has ended. Select the result to payout the winnings.'}
                    </p>
                  )}

                  <Button
                    className={cn(
                      "w-full h-14 text-base font-black uppercase tracking-widest transition-all duration-300 relative overflow-hidden group",
                      isEnded 
                        ? "bg-red-600 hover:bg-red-500 text-white shadow-lg" 
                        : "bg-secondary hover:bg-secondary/80 text-foreground"
                    )}
                    onClick={() => setShowResolveModal(true)}
                    data-testid="resolve-market-btn"
                  >
                    <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {language === 'de' ? 'ERGEBNIS FESTLEGEN' : 'DETERMINE RESULT'}
                    </span>
                  </Button>
                </div>
              </div>
            )}

            {/* Cancel Button */}
            {canCancel && (
              <Button
                variant="outline"
                className="w-full border-red-600/50 text-red-500 hover:bg-red-600/10 h-12"
                onClick={() => setShowCancelModal(true)}
                data-testid="cancel-market-btn"
              >
                <Ban className="w-4 h-4 mr-2" />
                {t('cancelMarket')}
              </Button>
            )}

            {/* Footer tags & info */}
            <div className="flex flex-col gap-4 pt-2 border-t border-border/10">
              {/* Tagged Users */}
              {market.tagged_users && market.tagged_users.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                  {market.tagged_users.map((username, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">@{username}</Badge>
                  ))}
                </div>
              )}

              {/* Visibility Info */}
              {market.visibility && market.visibility.type !== 'public' && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {visibilityIcon()}
                  <span>
                    {market.visibility.type === 'email_domain' && `@${market.visibility.email_domain}`}
                    {market.visibility.type === 'specific_users' && (language === 'de' ? 'Nur eingeladene Nutzer' : 'Invited users only')}
                    {market.visibility.type === 'group' && (language === 'de' ? 'Nur Gruppenmitglieder' : 'Group members only')}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sibling Tiers Comparison Table */}
        {renderTieredTable()}

        {/* Market Graph - Betting History */}
        <div className="bg-card border border-border/50 rounded-xl p-3 sm:p-6">
          <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            {language === 'de' ? 'Kursverlauf' : 'Odds History'}
          </h3>
          <div className="h-[200px] sm:h-[300px] w-full">
            <MarketGraph marketId={id} />
          </div>
        </div>

        {/* Winners Display */}
        {(market.status === 'resolved' || market.status === 'cancelled') && (
          <WinnersDisplay market={market} winners={winners} />
        )}

        {/* Payout Instructions */}
        {market.status === 'resolved' && payouts.length > 0 && (
          <Card className="border-border/50 overflow-hidden shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-700">
            <CardHeader className="bg-gradient-to-r from-yellow-500/10 via-primary/5 to-yellow-500/10 border-b border-border/10">
              <CardTitle className="text-xl flex items-center gap-3">
                <Wallet className="w-6 h-6 text-yellow-500" />
                {language === 'de' ? 'Zahlungsanweisungen' : 'Payout Instructions'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'de' 
                  ? 'Hier siehst du, wer wen bezahlen muss. Die Gewinner müssen den Erhalt bestätigen.' 
                  : 'Here you can see who has to pay whom. Winners must confirm receipt.'}
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border/10">
                {payouts.map((p) => {
                  const isPayer = p.payer_id === user?.id;
                  const isPayee = p.payee_id === user?.id;
                  
                  return (
                    <div key={p.id} className={cn(
                      "p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors",
                      isPayer ? "bg-red-500/5" : isPayee ? "bg-green-500/5" : ""
                    )}>
                      <div className="flex items-center gap-4">
                        <div className="flex -space-x-2">
                           <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center border-2 border-background text-[10px] font-bold">
                             {p.payer_username[0].toUpperCase()}
                           </div>
                           <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center border-2 border-background text-[10px] font-bold">
                             {p.payee_username[0].toUpperCase()}
                           </div>
                        </div>
                        <div className="flex flex-col">
                          <p className="text-sm font-bold">
                            <span className={cn(isPayer && "text-red-400 font-black italic underline")} title={`@${p.payer_username}`}>
                              {p.payer_nickname ? `${p.payer_nickname} (@${p.payer_username})` : `@${p.payer_username}`}
                            </span>
                            <span className="mx-2 text-muted-foreground font-normal">→</span>
                            <span className={cn(isPayee && "text-green-400 font-black italic underline")} title={`@${p.payee_username}`}>
                              {p.payee_nickname ? `${p.payee_nickname} (@${p.payee_username})` : `@${p.payee_username}`}
                            </span>
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {p.amount.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €
                            </Badge>
                            {p.status === 'confirmed' ? (
                              <Badge className="bg-green-600/20 text-green-400 border-green-600/30 text-[10px] py-0">
                                {language === 'de' ? 'Bezahlt' : 'Paid'}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] py-0 animate-pulse">
                                {language === 'de' ? 'Offen' : 'Pending'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {isPayer && p.status === 'pending' && p.payee_paypal_link && (
                          <Button 
                            size="sm" 
                            className="bg-[#0070ba] hover:bg-[#005ea6] text-white gap-2 h-9"
                            onClick={() => window.open(`${p.payee_paypal_link}/${p.amount.toFixed(2)}`, '_blank')}
                          >
                            <Send className="w-3.5 h-3.5" />
                            PayPal.me
                          </Button>
                        )}
                        
                        {isPayee && p.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-green-600/50 text-green-400 hover:bg-green-600/10 h-9"
                            onClick={() => confirmPayout(p.id)}
                          >
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                            {language === 'de' ? 'Erhalt bestätigen' : 'Confirm Receipt'}
                          </Button>
                        )}
                        
                        {!p.payee_paypal_link && isPayer && p.status === 'pending' && (
                          <span className="text-[10px] text-yellow-500 italic max-w-[150px] text-right leading-tight">
                            {language === 'de' 
                              ? 'Gewinner hat keinen PayPal-Link hinterlegt.' 
                              : 'Winner has no PayPal link provided.'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* User's Bets on this Market */}
        {userBets.length > 0 && (
          <Card data-testid="user-bets-card" className="border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg">{t('yourBets')}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="space-y-3">
                {userBets.map(bet => (
                  <div key={bet.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border/5 space-y-3 sm:space-y-0">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <Badge className={cn("px-2 py-0.5 text-[10px] font-bold", (bet?.position || 'yes') === 'yes' ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-red-600/20 text-red-400 border-red-600/30')}>
                          {bet?.position?.toUpperCase()}
                        </Badge>
                        {bet.status === 'won' ? (
                          <span className="font-mono font-bold text-sm text-green-400">
                            +{Math.max(0, (bet.payout || bet.potential_payout || 0) - (bet.amount || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        ) : bet.status === 'lost' ? (
                          <span className="font-mono font-bold text-sm text-red-400">
                            -{(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        ) : bet.status === 'refunded' ? (
                          <span className="font-mono font-bold text-sm text-muted-foreground">
                            0,00 €
                          </span>
                        ) : (
                          <span className="font-mono font-bold text-sm text-yellow-500">
                            {(bet?.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground ml-1">
                        {new Date(bet.created_at).toLocaleString(undefined, { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                        {(bet.status === 'won' || bet.status === 'lost' || bet.status === 'refunded') && (
                          <span className="ml-2 font-normal text-muted-foreground">
                            ({language === 'de' ? 'Einsatz:' : 'Stake:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end sm:gap-4">
                      <div className="text-right sm:order-2">
                        <Badge variant="outline" className={cn("text-[10px] uppercase font-bold tracking-widest px-2 py-0.5", 
                          bet.status === 'won' ? 'text-green-400 border-green-600/30 bg-green-600/5' : 
                          bet.status === 'lost' ? 'text-red-400 border-red-600/30 bg-red-600/5' : 
                          'text-muted-foreground border-border/30'
                        )}>
                          {t(bet.status)}
                        </Badge>
                      </div>
                      <div className="text-right sm:order-1">
                        {bet.status === 'pending' && (
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                            {t('potential')}: <span className="text-yellow-500 font-bold">{(calculatePotentialWin(bet.amount, bet.position, true) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Group Members Section */}
        {renderGroupMembers()}

        {/* Authorized Users Section */}
        {renderAllowedUsers()}

        {/* Comments Section */}
        <Card className="border-border/50">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-red-500" />
              {language === 'de' ? 'Diskussion' : 'Discussion'}
              <Badge variant="secondary" className="ml-auto">{comments.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 space-y-6">
            {/* Comment Form */}
            <form onSubmit={submitComment} className="flex gap-2">
              <Input 
                placeholder={language === 'de' ? 'Schreibe einen Kommentar...' : 'Write a comment...'}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" size="icon" disabled={!newComment.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </form>

            {/* Comments List */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {comments.length > 0 ? (
                comments.map(c => (
                  <div key={c.id} className="p-3 rounded-xl bg-secondary/20 border border-border/10 flex gap-3 items-start">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={c.avatar_url} alt={c.username} className="object-cover" />
                      <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-xs">
                        {c.username?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-sm text-red-400" title={`@${c.username}`}>
                          {c.nickname ? `${c.nickname} (@${c.username})` : `@${c.username}`}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(c.created_at).toLocaleString(undefined, {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed">{c.comment}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-xs text-muted-foreground italic">
                    {language === 'de' ? 'Noch keine Kommentare. Sei der Erste!' : 'No comments yet. Be the first!'}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Global Market Bets History */}
        <Card className="border-border/50">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg">
              {language === 'de' ? 'Wett-Historie' : 'Bet History'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {marketBets.length > 0 ? (
              <div className="space-y-3">
                {marketBets.map(bet => (
                  <div key={bet.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/10 border border-border/5">
                    <div className="flex flex-col">
                      <p className="font-bold text-sm" title={`@${bet.username}`}>
                        {bet.nickname ? `${bet.nickname} (@${bet.username})` : `@${bet.username}`}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(bet.created_at).toLocaleString(undefined, { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={cn("px-1.5 py-0 text-[10px] font-bold", (bet?.position || 'yes') === 'yes' ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-red-600/20 text-red-400 border-red-600/30')}>
                        {bet?.position?.toUpperCase()}
                      </Badge>
                      <span className="font-mono font-bold text-sm text-yellow-500">
                        {bet.amount.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {language === 'de' ? 'Noch keine Wetten platziert.' : 'No bets placed yet.'}
              </p>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Bet Modal */}
      <Dialog open={showBetModal} onOpenChange={setShowBetModal}>
        <DialogContent data-testid="bet-modal" className="sm:max-w-md border-none bg-card/95 backdrop-blur-xl shadow-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-2xl font-black text-center uppercase tracking-tight">
              {t('placeBet')}
            </DialogTitle>
            {betTargetMarket && (
              <p className="text-xs text-center text-muted-foreground mt-2 px-4 italic line-clamp-2">
                {betTargetMarket.title}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Position Selection Visualizer */}
            <div className="flex justify-center gap-4">
              <div className={cn(
                "flex-1 flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-300",
                selectedPosition === 'yes' 
                  ? "bg-green-500/20 border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105" 
                  : "bg-secondary/20 border-transparent opacity-40 grayscale-[0.5]"
              )}>
                <CheckCircle className={cn("w-10 h-10 mb-2", selectedPosition === 'yes' ? "text-green-400" : "text-muted-foreground")} />
                <span className="font-black text-lg">{t('yes').toUpperCase()}</span>
              </div>

              <div className={cn(
                "flex-1 flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-300",
                selectedPosition === 'no' 
                  ? "bg-red-500/20 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)] scale-105" 
                  : "bg-secondary/20 border-transparent opacity-40 grayscale-[0.5]"
              )}>
                <XCircle className={cn("w-10 h-10 mb-2", selectedPosition === 'no' ? "text-red-400" : "text-muted-foreground")} />
                <span className="font-black text-lg">{t('no').toUpperCase()}</span>
              </div>
            </div>

            <div className="space-y-4 bg-secondary/30 p-5 rounded-2xl border border-white/5">
              <div className="flex justify-between items-end mb-1">
                <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">{t('betAmount')}</Label>
                <span className="text-xs font-mono text-muted-foreground">
                  {t('yourBalance')}: {(user?.balance || 0) >= 0 ? '+' : ''}{user?.balance?.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €
                </span>
              </div>
              
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold opacity-40 group-focus-within:opacity-100 transition-opacity">€</div>
                <Input
                  type="number"
                  step="0.01"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  min={Math.max(1.00, betTargetMarket?.last_bet_amount || 1.00)}
                  max={50.00}
                  className="pl-10 h-16 text-3xl font-black bg-background/50 border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 transition-all"
                  data-testid="bet-amount-input"
                />
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[5, 10, 25, 50].map(amount => (
                  <Button
                    key={amount}
                    variant="secondary"
                    size="sm"
                    className="h-10 font-bold hover:bg-primary hover:text-primary-foreground transition-all rounded-lg"
                    onClick={() => setBetAmount(amount.toFixed(2))}
                    disabled={amount < (betTargetMarket?.last_bet_amount || 1.00)}
                  >
                    +{amount}
                  </Button>
                ))}
              </div>

              {(betTargetMarket?.last_bet_amount || 0) > 0 && (
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground bg-background/30 p-2 rounded-md">
                  <Tag className="w-3 h-3" />
                  <span>{language === 'de' ? 'Mindesteinsatz' : 'Min bet'}: {betTargetMarket.last_bet_amount.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €</span>
                </div>
              )}

              {/* Potential Win Display */}
              <div className="pt-2 mt-2 border-t border-white/5">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                    {language === 'de' ? 'Möglicher Gewinn' : 'Potential Payout'}
                  </span>
                  <span className="text-xl font-black text-primary drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]">
                    {potentialWin.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </span>
                </div>
                <p className="text-[9px] text-muted-foreground/60 leading-tight mt-1">
                  {language === 'de' 
                    ? '* Schätzung. Der tatsächliche Gewinn hängt vom finalen Pool-Verhältnis ab.' 
                    : '* Estimate. Actual payout depends on the final pool ratio.'}
                </p>
              </div>
            </div>
            
            <div className="px-1">
               <p className="text-[10px] text-center text-yellow-500/80 italic leading-tight">
                {language === 'de' 
                  ? 'Hinweis: Du kannst auch mit negativem Gewinn/Verlust (im Minus) teilnehmen! Scherenmarket vertraut dir.' 
                  : 'Note: You can participate even with a negative result (in the red)! Scherenmarket trusts you.'}
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2 mt-2">
            <Button
              className={cn(
                "w-full h-14 text-xl font-black uppercase tracking-tighter shadow-lg transition-all active:scale-95",
                selectedPosition === 'yes' ? "btn-yes" : "btn-no"
              )}
              onClick={submitBet}
              disabled={submitting || betAmount <= 0}
              data-testid="confirm-bet-btn"
            >
              {submitting ? t('placingBet') : `${t('betOn')} ${selectedPosition?.toUpperCase()}`}
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground hover:bg-transparent" onClick={() => setShowBetModal(false)}>
              {t('cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
              <DialogContent className="sm:max-w-[500px] border-none bg-card/95 backdrop-blur-2xl shadow-[0_0_50px_-12px_rgba(0,0,0,0.5)] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />
                
                <DialogHeader className="pb-4 relative">
                  <DialogTitle className="text-3xl font-black text-center uppercase tracking-tighter italic flex items-center justify-center gap-3">
                    <Edit className="w-8 h-8 text-primary" />
                    {language === 'de' ? 'Wette anpassen' : 'Edit Market'}
                  </DialogTitle>
                  <div className="h-1 w-20 bg-primary mx-auto mt-2 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                </DialogHeader>

                <div className="space-y-6 py-6 relative">
                  <div className="space-y-3 group">
                    <Label htmlFor="edit-title" className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">
                      {t('questionTitle')}
                    </Label>
                    <div className="relative">
                      <Input
                        id="edit-title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder={t('questionPlaceholder')}
                        className="h-12 bg-background/50 border-white/5 focus:border-primary/50 focus:ring-primary/20 transition-all text-lg font-bold pl-4"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground/30">
                        {editTitle.length}/200
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 group">
                    <Label htmlFor="edit-description" className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">
                      {t('descriptionOptional')}
                    </Label>
                    <Textarea
                      id="edit-description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder={t('descriptionPlaceholder')}
                      rows={5}
                      className="bg-background/50 border-white/5 focus:border-primary/50 focus:ring-primary/20 transition-all resize-none text-sm leading-relaxed"
                    />
                  </div>

                  {user?.is_admin && (
                    <div className="space-y-4 pt-2 border-t border-white/5">
                      <div className="space-y-3">
                        <Label className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">
                          {t('visibility')} (Admin)
                        </Label>
                        <Select value={editVisibilityType} onValueChange={setEditVisibilityType}>
                          <SelectTrigger className="h-12 bg-background/50 border-white/5 focus:border-primary/50 focus:ring-primary/20 transition-all font-bold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-white/10">
                            <SelectItem value="public">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4" />
                                {t('visibilityPublic')}
                              </div>
                            </SelectItem>
                            <SelectItem value="group">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {t('visibilityGroup')}
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {editVisibilityType === 'group' && (
                        <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                          <Label className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">
                            {t('selectGroup')}
                          </Label>
                          <Select value={editSelectedGroup} onValueChange={setEditSelectedGroup}>
                            <SelectTrigger className="h-12 bg-background/50 border-white/5 focus:border-primary/50 focus:ring-primary/20 transition-all font-bold">
                              <SelectValue placeholder={t('selectGroup')} />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              {groups.map(group => (
                                <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-primary/5 p-4 rounded-xl border border-primary/10 flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <TrendingUp className="w-4 h-4 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-primary uppercase tracking-wider">
                        {language === 'de' ? 'Wichtiger Hinweis' : 'Important Note'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {language === 'de' 
                          ? 'Änderungen sind nur möglich, solange noch niemand gewettet hat. Nutze diese Chance, um Tippfehler zu korrigieren oder die Beschreibung zu präzisieren.' 
                          : 'Changes are only possible as long as no one has placed a bet. Use this opportunity to fix typos or clarify the description.'}
                      </p>
                    </div>
                  </div>
                </div>

                <DialogFooter className="flex-col sm:flex-col gap-3 mt-4 relative">
                  <Button 
                    onClick={updateMarket} 
                    disabled={submitting}
                    className="w-full h-14 text-xl font-black uppercase tracking-tighter shadow-[0_10px_20px_-10px_rgba(225,29,72,0.4)] hover:shadow-[0_15px_30px_-10px_rgba(225,29,72,0.6)] transition-all active:scale-95 bg-primary hover:bg-primary/90"
                  >
                    {submitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('saving')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-6 h-6" />
                        {t('saveChanges')}
                      </div>
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowEditModal(false)} 
                    disabled={submitting}
                    className="w-full text-muted-foreground hover:text-white hover:bg-white/5 font-bold uppercase tracking-widest text-[10px]"
                  >
                    {t('cancel')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Resolve Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent data-testid="resolve-modal" className="sm:max-w-md border-none bg-card/95 backdrop-blur-2xl shadow-[0_0_50px_-12px_rgba(0,0,0,0.5)] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />
          
          <DialogHeader className="pb-4 relative">
            <DialogTitle className="text-3xl font-black text-center uppercase tracking-tighter italic">
              {language === 'de' ? 'Wette Auflösen' : 'Resolve Market'}
            </DialogTitle>
            <div className="h-1 w-20 bg-primary mx-auto mt-2 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
          </DialogHeader>

          <div className="space-y-6 py-4 relative">
            <div className="text-center space-y-2">
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest px-6 leading-tight">
                {language === 'de' ? 'Wähle das endgültige Ergebnis dieser Wette:' : 'Select the final outcome for this market:'}
              </p>
              <div className="flex items-center justify-center gap-2 text-[10px] text-red-400 font-black uppercase tracking-tighter bg-red-400/10 py-1 px-3 rounded-full w-fit mx-auto border border-red-400/20">
                <Ban className="w-3 h-3" />
                {language === 'de' ? 'Diese Aktion ist endgültig' : 'This action is final'}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 px-2">
              <Button
                className="group relative h-28 bg-green-500/10 hover:bg-green-500/20 border-2 border-green-500/30 hover:border-green-500 transition-all duration-500 rounded-2xl overflow-hidden shadow-lg hover:shadow-green-500/20"
                onClick={() => resolveMarket('yes')}
                disabled={submitting}
                data-testid="resolve-yes-btn"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center justify-between w-full px-8 relative z-10">
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-green-400/70 mb-1">{t('yes')}</p>
                    <p className="text-2xl font-black text-white group-hover:scale-105 transition-transform origin-left">{t('yesWon').toUpperCase()}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-500 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500" />
                </div>
              </Button>

              <Button
                className="group relative h-28 bg-red-500/10 hover:bg-red-500/20 border-2 border-red-500/30 hover:border-red-500 transition-all duration-500 rounded-2xl overflow-hidden shadow-lg hover:shadow-red-500/20"
                onClick={() => resolveMarket('no')}
                disabled={submitting}
                data-testid="resolve-no-btn"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center justify-between w-full px-8 relative z-10">
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400/70 mb-1">{t('no')}</p>
                    <p className="text-2xl font-black text-white group-hover:scale-105 transition-transform origin-left">{t('noWon').toUpperCase()}</p>
                  </div>
                  <XCircle className="w-12 h-12 text-red-500 group-hover:scale-110 group-hover:-rotate-12 transition-all duration-500" />
                </div>
              </Button>
            </div>
          </div>

          <DialogFooter className="relative bg-secondary/20 p-4 -mx-6 -mb-6 border-t border-white/5">
            <Button 
              variant="ghost" 
              className="w-full text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-white hover:bg-white/5 transition-all" 
              onClick={() => setShowResolveModal(false)}
            >
              {t('cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent data-testid="cancel-modal" className="sm:max-w-md border-none bg-card/95 backdrop-blur-2xl shadow-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent pointer-events-none" />
          
          <DialogHeader className="pb-2">
            <DialogTitle className="text-2xl font-black text-center uppercase tracking-tight flex items-center justify-center gap-2 text-red-500">
              <Ban className="w-6 h-6" />
              {t('cancelMarket')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4 text-center">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
              <p className="text-sm font-medium text-red-200 leading-relaxed">
                {t('cancelMarketConfirm')}
              </p>
              <p className="text-[10px] text-red-400/70 uppercase font-black mt-3 tracking-widest">
                {language === 'de' ? 'Alle Einsätze werden zurückerstattet' : 'All bets will be refunded'}
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="ghost" 
              className="flex-1 order-2 sm:order-1 text-muted-foreground" 
              onClick={() => setShowCancelModal(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              className="flex-1 order-1 sm:order-2 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-tighter h-12 shadow-lg shadow-red-600/20 active:scale-95 transition-all"
              onClick={cancelMarket}
              disabled={submitting}
              data-testid="confirm-cancel-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              {language === 'de' ? 'WETTE ABBRECHEN' : 'CANCEL MARKET'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manage Users Modal */}
      <Dialog open={showManageUsersModal} onOpenChange={setShowManageUsersModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-yellow-500" />
              {language === 'de' ? 'Berechtigungen verwalten' : 'Manage Permissions'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'de' ? 'Benutzer hinzufügen' : 'Add User'}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('searchUsers')}
                  value={userSearch}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              {userSearchResults.length > 0 && (
                <div className="absolute z-50 w-[calc(100%-3rem)] bg-card border border-border rounded-lg shadow-lg mt-1 overflow-hidden">
                  {userSearchResults.slice(0, 5).map(u => (
                    <div
                      key={u.id}
                      className="px-3 py-2 hover:bg-secondary cursor-pointer flex justify-between items-center"
                      onClick={() => addAllowedUser(u)}
                    >
                      <span>@{u.username}</span>
                      <Plus className="w-3 h-3 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>{language === 'de' ? 'Berechtigte Personen' : 'Authorized Users'}</Label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {tempAllowedUsers.map(u => (
                  <Badge key={u.id} variant="secondary" className="gap-1 px-2 py-1">
                    @{u.username}
                    <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeAllowedUser(u.id)} />
                  </Badge>
                ))}
                {tempAllowedUsers.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Keine Benutzer ausgewählt.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageUsersModal(false)}>{t('cancel')}</Button>
            <Button onClick={saveAllowedUsers} disabled={submitting}>
              {submitting ? t('saving') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ManualCopyDialog 
        open={showCopyDialog} 
        onOpenChange={setShowCopyDialog} 
        link={linkToCopy} 
        t={t} 
      />
    </div>
  );
};

const ManualCopyDialog = ({ open, onOpenChange, link, t }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('copyLinkManually')}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center space-x-2 py-4">
          <div className="grid flex-1 gap-2">
            <Input
              readOnly
              value={link}
              className="font-mono text-sm"
            />
          </div>
          <Button
            size="sm"
            className="px-3"
            onClick={() => {
              navigator.clipboard.writeText(link);
              toast.success(t('inviteLinkCopied'));
              onOpenChange(false);
            }}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter className="sm:justify-start">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {t('close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarketDetail;
